import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { RefreshCw, Check, XCircle } from 'lucide-react';
import { unavailabilityRequestApi } from '../services/api';
import { UnavailabilityRequest } from '../types';
import { useLocationContext } from '../components/Layout';
import { formatCompanyDate } from '../utils/companyDateFormat';

const UnavailabilityAdmin: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { selectedLocationId } = useLocationContext();
  const [requests, setRequests] = useState<UnavailabilityRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('Pending');
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [rejecting, setRejecting] = useState<UnavailabilityRequest | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await unavailabilityRequestApi.getAll(undefined, statusFilter || undefined);
      setRequests(Array.isArray(data) ? data : []);
    } catch {
      setRequests([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!selectedLocationId) return requests;
    return requests.filter((r) => !r.employee?.mainLocationId || r.employee.mainLocationId === selectedLocationId);
  }, [requests, selectedLocationId]);

  const highlightId = Number(searchParams.get('requestId') || 0) || null;

  const approve = async (id: number) => {
    try {
      setProcessingId(id);
      await unavailabilityRequestApi.approve(id);
      await load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      alert(ax.response?.data?.message || ax.message || 'Failed to approve.');
    } finally {
      setProcessingId(null);
    }
  };

  const confirmReject = async () => {
    if (!rejecting) return;
    try {
      setProcessingId(rejecting.id);
      await unavailabilityRequestApi.reject(rejecting.id, rejectReason.trim() || 'Not approved');
      setRejecting(null);
      setRejectReason('');
      await load();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      alert(ax.response?.data?.message || ax.message || 'Failed to reject.');
    } finally {
      setProcessingId(null);
    }
  };

  if (!selectedLocationId) {
    return (
      <div className="flex h-64 items-center justify-center text-neutral-500">
        Select a location from the top navigation to manage unavailability.
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-800">Unavailability Admin</h1>
          <p className="text-sm text-neutral-500 mt-1">Approve or deny employee unavailability requests.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-neutral-300 rounded-md text-sm"
          >
            <option value="">All statuses</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Denied">Denied</option>
            <option value="Cancelled">Cancelled</option>
          </select>
          <button
            type="button"
            onClick={() => { setRefreshing(true); load(); }}
            className="p-2 rounded-lg bg-neutral-100 hover:bg-neutral-200"
          >
            <RefreshCw className={`h-5 w-5 text-blue-600 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-neutral-500 py-12">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center text-neutral-500">
          No unavailability requests found.
        </div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-neutral-500">Employee</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-500">Pattern</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-500">Status</th>
                <th className="px-4 py-3 text-left font-medium text-neutral-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtered.map((req) => (
                <tr key={req.id} className={highlightId === req.id ? 'bg-amber-50' : undefined}>
                  <td className="px-4 py-3">
                    <div className="font-medium">
                      {req.employee?.firstName} {req.employee?.lastName}
                    </div>
                    {req.reason && <div className="text-xs text-neutral-500 mt-0.5">{req.reason}</div>}
                  </td>
                  <td className="px-4 py-3">{req.summary}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs border ${
                      req.status === 'Approved' ? 'bg-slate-100 text-slate-800 border-slate-200' :
                      req.status === 'Denied' ? 'bg-red-50 text-red-700 border-red-200' :
                      'bg-amber-50 text-amber-800 border-amber-200'
                    }`}>
                      {req.status}
                    </span>
                    {req.approvedByName && req.status === 'Approved' && (
                      <div className="text-xs text-neutral-500 mt-1">
                        {req.approvedByName}
                        {req.approvedAt ? ` · ${formatCompanyDate(req.approvedAt)}` : ''}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {req.status === 'Pending' && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          disabled={processingId === req.id}
                          onClick={() => approve(req.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-green-600 text-white rounded-md text-xs hover:bg-green-700 disabled:opacity-50"
                        >
                          <Check className="h-3.5 w-3.5" />
                          Approve
                        </button>
                        <button
                          type="button"
                          disabled={processingId === req.id}
                          onClick={() => { setRejecting(req); setRejectReason(''); }}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-red-600 text-white rounded-md text-xs hover:bg-red-700 disabled:opacity-50"
                        >
                          <XCircle className="h-3.5 w-3.5" />
                          Deny
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rejecting && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md space-y-3">
            <h2 className="text-lg font-semibold">Deny unavailability</h2>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full border rounded-md px-3 py-2 text-sm"
              rows={3}
              placeholder="Reason"
            />
            <div className="flex justify-end gap-2">
              <button type="button" className="px-3 py-2 border rounded-md" onClick={() => setRejecting(null)}>
                Close
              </button>
              <button
                type="button"
                className="px-3 py-2 bg-red-600 text-white rounded-md"
                onClick={confirmReject}
              >
                Deny
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnavailabilityAdmin;
